import type {
  AtividadeCasa,
  Autonomia,
  Boletim,
  Chegada,
  Comportamento,
  Desempenho,
  Foco,
} from './enums';

/** No lugar de um uuid em `id` quando a linha é "virtual" (horário do dia sem `RegistroAula`): agir nela exige `POST /registros`, não `PUT`/`GET /registros/:id`. */
export const VIRTUAL_REGISTRO_ID = 'virtual';

interface RegistroComNotas {
  chegada: Chegada | null;
  boletim: Boletim | null;
  atividadeCasa: AtividadeCasa | null;
  foco: Foco | null;
  autonomia: Autonomia | null;
  comportamento: Comportamento | null;
  desempenho: Desempenho | null;
}

interface RegistroComProgresso extends RegistroComNotas {
  relatorioTexto: string | null;
  relatorioEnviadoEm: Date | string | null;
}

/** Falta e completo por definicao: nao ha nota nenhuma pra dar quando o aluno nao veio. */
export function isFalta(registro: Pick<RegistroComNotas, 'chegada'>): boolean {
  return registro.chegada === 'FALTOU';
}

/** "Completo" é sempre calculado aqui, sem `fechado`/`status` no backend. `anotacao` e `conteudoIds` nunca entram na conta. */
export function isCompleto(registro: RegistroComProgresso): boolean {
  if (registro.chegada === null) {
    return false;
  }
  if (isFalta(registro)) {
    return true;
  }
  if (!registro.relatorioTexto?.trim() || registro.relatorioEnviadoEm === null) {
    return false;
  }

  return (
    registro.boletim !== null &&
    registro.atividadeCasa !== null &&
    registro.foco !== null &&
    registro.autonomia !== null &&
    registro.comportamento !== null &&
    registro.desempenho !== null
  );
}

const CAMPOS_NOTA = [
  'boletim',
  'atividadeCasa',
  'foco',
  'autonomia',
  'comportamento',
  'desempenho',
] as const satisfies readonly (keyof RegistroComNotas)[];

export function contarNotasPreenchidas(registro: RegistroComNotas): number {
  return CAMPOS_NOTA.filter((campo) => registro[campo] !== null).length;
}

export const TOTAL_ETAPAS_REGISTRO = 8;

/** Avaliações preenchidas + relatório gerado + relatório enviado. */
export function contarEtapasConcluidas(registro: RegistroComProgresso): number {
  const relatorioGerado = registro.relatorioTexto?.trim() ? 1 : 0;
  const relatorioEnviado = registro.relatorioEnviadoEm !== null ? 1 : 0;
  return contarNotasPreenchidas(registro) + relatorioGerado + relatorioEnviado;
}

export type StatusRegistro = 'NAO_INICIADO' | 'PENDENTE' | 'CONCLUIDO';

/** Os 3 estados da lista diária, derivados do preenchimento: "Não iniciado" = linha virtual; "Pendente" = existe mas não `isCompleto`; "Concluído" = `isCompleto`. */
export function statusRegistro(registro: { id: string } & RegistroComProgresso): StatusRegistro {
  if (registro.id === VIRTUAL_REGISTRO_ID) {
    return 'NAO_INICIADO';
  }
  if (isCompleto(registro)) {
    return 'CONCLUIDO';
  }
  return 'PENDENTE';
}

export type CampoPreview = 'chegada' | 'comportamento' | 'atividadeCasa' | 'desempenho';

/** Preview resumido (chips do `RegistroRow`, resumo do `RegistrarAulaDialog`): `chegada` só se `ATRASADO`/`FALTOU` e só 3 notas (comportamento, atividade de casa, desempenho) — decisão de produto. */
export function camposPreview(registro: RegistroComNotas): CampoPreview[] {
  const campos: CampoPreview[] = [];
  if (registro.chegada === 'ATRASADO' || registro.chegada === 'FALTOU') {
    campos.push('chegada');
  }
  if (registro.comportamento !== null) {
    campos.push('comportamento');
  }
  if (registro.atividadeCasa !== null) {
    campos.push('atividadeCasa');
  }
  if (registro.desempenho !== null) {
    campos.push('desempenho');
  }
  return campos;
}
